import * as React from "react"
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react"
import { TableHead } from "@/components/ui/table"

export type SortDirection = "asc" | "desc"

export interface SortableHeaderProps<T extends string> {
  label: string
  sortKey: T
  currentSortColumn: T | null
  currentSortDirection: SortDirection
  onSort: (column: T) => void
  disabled?: boolean
}

export function SortableHeader<T extends string>({
  label,
  sortKey,
  currentSortColumn,
  currentSortDirection,
  onSort,
  disabled = false,
}: SortableHeaderProps<T>) {
  const isActive = currentSortColumn === sortKey
  const sortLabel = isActive
    ? `${label}, sorted ${currentSortDirection === "asc" ? "ascending" : "descending"}`
    : `${label}, click to sort`
  return (
    <TableHead
      className={`text-muted-foreground font-medium select-none transition-colors ${
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:text-foreground"
      }`}
      onClick={() => {
        if (!disabled) onSort(sortKey)
      }}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault()
          onSort(sortKey)
        }
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-sort={isActive ? (currentSortDirection === "asc" ? "ascending" : "descending") : undefined}
      aria-label={sortLabel}
    >
      <div className="flex items-center gap-1.5">
        {label}
        {isActive ? (
          currentSortDirection === "asc" ? (
            <ArrowDown className="h-4 w-4" aria-hidden />
          ) : (
            <ArrowUp className="h-4 w-4" aria-hidden />
          )
        ) : (
          <ArrowUpDown className="h-4 w-4 opacity-50" aria-hidden />
        )}
      </div>
    </TableHead>
  )
}
